"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.HourlyYearBasedNormalAccumulator = exports.NormalAccumulator = exports.hourlyYearBasedAccessorFromTimestamp = exports.aggregateCMVs = void 0;
const tslib_1 = require("tslib");
const iotv_v3_types_1 = require("@iotv/iotv-v3-types");
const enumerableDecorator_1 = require("../../enumerableDecorator");
const objectOperations_1 = require("../../../../util/objects/objectOperations");
const ModelCommon_1 = require("../../../ModelCommon");
const CMVarAggregation_1 = require("../../CMVarAggregation");
const sharedTypes_1 = require("../sharedTypes");
const util_1 = require("../../../../util");
const className = 'NormalAccumulator';
/**
 * Aggregates the results of Coverage Map Variables into a single {@link CMVarAggregationObject}
 *
 * @param cmvs Coverage Map Variable Aggregations
 * @param accumulateMinMax Whether to take the min or max of the supplied {@link CMVarAggregation}, or to accumulate them all together
 * @returns
 */
const aggregateCMVs = (cmvs, accumulateMinMax) => {
    const aggrCMV = new CMVarAggregation_1.CMVarAggregationObject();
    cmvs.forEach((cmv) => {
        aggrCMV.mean = (aggrCMV.mean * aggrCMV.cn + cmv.mean * cmv.cn) / (aggrCMV.cn + cmv.cn);
        aggrCMV.cn += cmv.cn;
        aggrCMV.firstTimestamp = aggrCMV.firstTimestamp
            ? cmv.firstTimestamp
                ? Math.min(aggrCMV.firstTimestamp, cmv.firstTimestamp)
                : aggrCMV.firstTimestamp
            : cmv.firstTimestamp;
        aggrCMV.lastTimeStamp = aggrCMV.lastTimeStamp
            ? cmv.lastTimeStamp
                ? Math.max(aggrCMV.lastTimeStamp, cmv.lastTimeStamp)
                : aggrCMV.lastTimeStamp
            : cmv.lastTimeStamp;
        if (accumulateMinMax) {
            aggrCMV.max = (aggrCMV.max ?? 0) + (cmv.max ?? 0);
            aggrCMV.min = (aggrCMV.min ?? 0) + (cmv.min ?? 0);
        }
        else {
            aggrCMV.max = Math.max(aggrCMV.max ?? cmv.max ?? -Infinity, cmv.max ?? aggrCMV.max ?? -Infinity);
            aggrCMV.min = Math.min(aggrCMV.min ?? cmv.min ?? Infinity, cmv.min ?? aggrCMV.min ?? Infinity);
        }
        aggrCMV.sqDiffFromMean += cmv.sqDiffFromMean; // This is not accurate!!!
    });
    if (aggrCMV.min === Infinity)
        aggrCMV.min = undefined;
    if (aggrCMV.max === -Infinity)
        aggrCMV.max = undefined;
    return aggrCMV;
};
exports.aggregateCMVs = aggregateCMVs;
/** Get accessor from timestamp
 * Doesn't deal with local time, but if you always access through the accessor you'll be fine.
 * This does mean there's some weirdness with your local timezone if you view the accumulator directly
 * E.g. in NZ, the normal for 4 Dec 14:00 is actually in [11][3][1] (month 11, day 3, hour 1)
 * TODO: is this affected by daylight savings? God I hope not
 */
const hourlyYearBasedAccessorFromTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const idx = {
        month: date.getUTCMonth(),
        day: (date.getUTCDate() - 1),
        hour: date.getUTCHours(),
    };
    return idx;
};
exports.hourlyYearBasedAccessorFromTimestamp = hourlyYearBasedAccessorFromTimestamp;
class NormalAccumulator extends ModelCommon_1.ModelCommon {
    constructor(ob) {
        super(ob?.type ?? className, (({ sk, pk, type, id, name }) => ({ sk, pk, type, id, name }))(ob ?? {}));
        if (ob) {
            if (ob.accumulator instanceof Array) {
                this.ingest(ob);
            }
            else {
                this.accumulator = ob.accumulator;
                this.keyMap = ob.keyMap;
            }
        }
    }
}
exports.NormalAccumulator = NormalAccumulator;
class HourlyYearBasedNormalAccumulator extends NormalAccumulator {
    constructor(ob) {
        super(ob);
        this.initialize = () => {
            const xFn = () => ({});
            const dehydrated = { accumulator: (0, objectOperations_1.fillWithXByArrayDef)(sharedTypes_1.HourlyYearBasedArrayDef, xFn)() };
            this.ingest(dehydrated);
        };
        this.getDimensions = () => {
            let dimStr = [];
            let map = this.accumulator;
            do {
                map && dimStr.push(Object.keys(map).length);
                map = map?.[0];
            } while (map);
            return dimStr;
        };
        this.xFn = () => undefined;
        this.ingest = ({ accumulator: HourlyYearBasedAccumulation, keyMap, sk, pk, type, id, name }) => {
            this.keyMap = keyMap; // this must be intiialized first otherwise will get bad keys
            const months = {};
            Object.entries(HourlyYearBasedAccumulation).forEach(([i, dayArray]) => {
                const days = {};
                Object.entries(dayArray).forEach(([i, hourArray]) => {
                    const hours = {};
                    Object.entries(hourArray).forEach(([i, vmvLite]) => {
                        const cmv = {};
                        Object.entries(vmvLite).forEach(([shortCMVKey, v]) => (cmv[this.getOriginalCMVKey(shortCMVKey)] = (0, iotv_v3_types_1.CMVArrayToMap)(vmvLite[shortCMVKey])));
                        hours[parseInt(i)] = cmv;
                    });
                    days[parseInt(i)] = hours;
                });
                months[parseInt(i)] = days;
            });
            this.accumulator = months;
            sk && (this.sk = sk);
            pk && (this.pk = pk);
            type && (this.type = type);
            id && (this.id = id);
            name && (this.name = name);
        };
        this.egest = () => this.accumulator
            ? { ...this.dehydrate(this.accumulator), keyMap: this.keyMap, type: this.type, pk: this.pk, sk: this.sk, id: this.id, name: this.name }
            : undefined;
        this.getShortCMVKey = (k) => {
            let shortCMVKey = k;
            if (!this.keyMap) {
                this.keyMap = {};
            }
            if (this.keyMap?.[k]) {
                shortCMVKey = this.keyMap[k];
            }
            else {
                const keyMapEntriesNum = Object.keys(this.keyMap).length;
                shortCMVKey = String.fromCharCode(keyMapEntriesNum + 65);
                this.keyMap[k] = shortCMVKey;
            }
            return shortCMVKey;
        };
        this.getOriginalCMVKey = (shortKey) => {
            let originalCMVKey = shortKey;
            if (this.keyMap) {
                const matchedEntry = Object.entries(this.keyMap).find(([k, v]) => v === shortKey);
                if (matchedEntry) {
                    const [ok, v] = matchedEntry;
                    originalCMVKey = ok;
                }
            }
            return originalCMVKey;
        };
        this.dehydrate = (acc) => {
            const dehydrated = [];
            Object.values(acc).forEach((months, m) => {
                dehydrated[m] = [];
                Object.values(months).forEach((days, d) => {
                    dehydrated[m][d] = [];
                    Object.values(days).forEach((hours, h) => {
                        if (hours) {
                            dehydrated[m][d][h] = Object.fromEntries(Object.entries(hours).map(([k, cmv]) => [this.getShortCMVKey(k), (0, iotv_v3_types_1.CMVMapToArray)(cmv)]));
                        }
                        else {
                            dehydrated[m][d][h] = {};
                        }
                    });
                });
            });
            return { accumulator: dehydrated };
        };
        this.getByTimestamp = (timestamp, cmvKey) => {
            const idx = this.idxFromTimestamp(timestamp, cmvKey);
            return this.getByIndex(idx);
        };
        this.getByIndex = ({ month, day, hour, cmvKey }) => {
            //console.log("Getting by index", month, day, hour);
            const cmv = this.accumulator?.[month][day][hour]?.[cmvKey];
            return cmv;
        };
        this.setByIndex = ({ month, day, hour, cmvKey }, cmv) => {
            if (this.accumulator?.[month]?.[day]?.[hour]) {
                const sector = this.accumulator[month][day][hour];
                if (sector) {
                    sector[cmvKey] = cmv;
                }
            }
            else {
                const msg = `Can't find accessor ${Object.values({ month, day, hour, cmvKey })}`;
                //console.log(`${msg} on this with accumulator`, this.accumulator)
                throw new Error(msg);
            }
        };
        // In datalifecycle, the timestamp isn't passed, so even if we update some time in the past, it will assume the update is happening now. Not really a huge deal though
        this.updateByIndex = (accessor, value, timestamp) => {
            if (value !== null && value !== undefined) {
                const cmvZombie = this.getByIndex(accessor);
                const cmv = new CMVarAggregation_1.CMVarAggregationObject(cmvZombie);
                cmv.update(value, timestamp);
                this.setByIndex(accessor, cmv);
            }
        };
        this.idxFromTimestamp = (timestamp, cmvKey) => {
            const idx = {
                ...(0, exports.hourlyYearBasedAccessorFromTimestamp)(timestamp),
                cmvKey,
            };
            return idx;
        };
        /** Gets the normal for a given period of time, assembled from hourly normals accumulated.
         * @param timestampRange - of the form [fromMs, toMs]
         * @param cmvKey - The key in the accumulator to get the normal for
         * @returns a CMV with the desired normal. Not that the CMV is not accumulated itself, such that you'll get the normal for the entire period. That is to say, if you give 2 hours, you'll get what is normal for two hours, not the hourly average within those two hours
         * */
        // TODO: add a way of getting prior period equal in length to the one we're getting and accumulating it
        this.getNormalForPeriod = (timestampRange, cmvKey) => {
            let debug = false;
            let cmvs = [];
            if (timestampRange[1] < timestampRange[0])
                return undefined;
            // Need to find the overhang
            //console.log(`Getting normal for ${new Date(timestampRange[0])} ${new Date(timestampRange[1])}`)
            const firstPortion = (iotv_v3_types_1.Milliseconds.HOUR - (timestampRange[0] % iotv_v3_types_1.Milliseconds.HOUR)) / iotv_v3_types_1.Milliseconds.HOUR;
            //console.log("First Portion", firstPortion);
            let lastPortion = (timestampRange[1] % iotv_v3_types_1.Milliseconds.HOUR) / iotv_v3_types_1.Milliseconds.HOUR;
            if (lastPortion < 0.001)
                lastPortion = 0;
            //console.log("Last Portion", lastPortion);
            // Scale just one CMV if range is contained within 1 hour
            if (timestampRange[1] - timestampRange[0] < iotv_v3_types_1.Milliseconds.HOUR /* Need to check they're within the same hour*/) {
                // Within one hour -> return one scaled CMV
                const firstIdx = (0, exports.hourlyYearBasedAccessorFromTimestamp)(timestampRange[0]);
                const lastIdx = (0, exports.hourlyYearBasedAccessorFromTimestamp)(timestampRange[1]);
                if (JSON.stringify(firstIdx) == JSON.stringify(lastIdx)) {
                    const portion = (timestampRange[1] - timestampRange[0]) / iotv_v3_types_1.Milliseconds.HOUR;
                    const cmv = this.getByTimestamp(timestampRange[0], cmvKey);
                    if (!cmv)
                        return undefined;
                    const singleScaledCMV = {
                        mean: cmv.mean * portion, // I don't know if this is sound
                        cn: cmv.cn * portion,
                        sqDiffFromMean: cmv.sqDiffFromMean * portion,
                        firstTimestamp: timestampRange[0],
                        lastTimeStamp: timestampRange[1],
                        min: cmv.min,
                        max: cmv.max,
                    };
                    return new CMVarAggregation_1.CMVarAggregationObject(singleScaledCMV);
                }
            }
            for (let t = timestampRange[0]; t < timestampRange[1] + iotv_v3_types_1.Milliseconds.HOUR; t += iotv_v3_types_1.Milliseconds.HOUR) {
                // Extended by an hour to catch the last CMV if we only need a portion
                const idx = this.idxFromTimestamp(t, cmvKey);
                const cmv = this.getByIndex(idx);
                if (!cmv)
                    continue;
                if (t == timestampRange[0]) {
                    // Scale CMV by portion
                    const scaledCmv = {
                        mean: cmv.mean * firstPortion, // I don't know if this is sound
                        cn: cmv.cn * firstPortion,
                        sqDiffFromMean: cmv.sqDiffFromMean * firstPortion,
                        firstTimestamp: timestampRange[0],
                        lastTimeStamp: cmv.lastTimeStamp,
                        min: cmv.min,
                        max: cmv.max,
                    };
                    cmvs.push(scaledCmv);
                }
                else if (t > timestampRange[1] - iotv_v3_types_1.Milliseconds.HOUR) {
                    if (lastPortion > 0) {
                        //console.log(lastPortion)
                        const scaledCmv = {
                            mean: cmv.mean * lastPortion, // I don't know if this is sound
                            cn: cmv.cn * lastPortion,
                            sqDiffFromMean: cmv.sqDiffFromMean * lastPortion,
                            firstTimestamp: cmv.firstTimestamp,
                            lastTimeStamp: timestampRange[1],
                            min: cmv.min,
                            max: cmv.max,
                        };
                        cmvs.push(scaledCmv);
                    }
                }
                else {
                    cmvs.push(cmv);
                }
                //let date = new Date(`${new Date(Date.now()).getFullYear()}-${idx.month}-${idx.day} ${idx.hour}:00`)
                //if (cmv) normalBucketed.push({ timestamp: date.valueOf(), state: cmv }, true, date.valueOf() - Milliseconds.HOUR)
            }
            if (cmvs.length == 0)
                return undefined;
            //console.log(cmvs);
            // const aggrNormal = aggregateCMVs(cmvs)
            //console.log(aggrNormal);
            // Given that we want the normal for a period longer than 1 hour, I think adding the mean is actually accurate ?
            // E.g. we have the normal for  hour 1 and hour 2, but we want the mean for a 2 hour period. Aggregating would give the mean for a 1 hour period with constituent values from only those two hours
            debug && console.log('CMVs found', cmvs);
            const addedNormal = cmvs.reduce((total, cmv) => {
                return {
                    mean: total.mean + cmv.mean,
                    cn: total.cn + cmv.cn,
                    sqDiffFromMean: (total.sqDiffFromMean ?? 0) + (cmv.sqDiffFromMean ?? 0), // This is completely wrong, idk if we can find this
                    firstTimestamp: (cmv.firstTimestamp ?? Infinity) < (total.firstTimestamp ?? Infinity) ? cmv.firstTimestamp : total.firstTimestamp,
                    lastTimeStamp: (cmv.lastTimeStamp ?? -Infinity) > (total.lastTimeStamp ?? -Infinity) ? cmv.lastTimeStamp : total.lastTimeStamp,
                    min: (cmv.min ?? Infinity) < (total.min ?? Infinity) ? cmv.min : total.min,
                    max: (cmv.max ?? -Infinity) > (total.max ?? -Infinity) ? cmv.max : total.max,
                };
            }, { mean: 0, cn: 0, sqDiffFromMean: 0, firstTimestamp: undefined, lastTimeStamp: undefined, min: Infinity, max: -Infinity });
            const addedCMV = new CMVarAggregation_1.CMVarAggregationObject(addedNormal);
            //console.log(addedCMV);
            return addedCMV;
        };
        /**
         * Aggregates (up to) a year's worth of readings and produces an average CMVar object for an hour
         *
         * It is safe to immediately use the `mean` (and `cn`) to represent an hour's worth of readings
         */
        this.getNormal = (cmvKey) => {
            let debug = false;
            let cmvs = [];
            const timestampRange = [Date.now() - iotv_v3_types_1.Milliseconds.DAY * 365, Date.now()];
            const firstPortion = (iotv_v3_types_1.Milliseconds.HOUR - (timestampRange[0] % iotv_v3_types_1.Milliseconds.HOUR)) / iotv_v3_types_1.Milliseconds.HOUR;
            let lastPortion = (timestampRange[1] % iotv_v3_types_1.Milliseconds.HOUR) / iotv_v3_types_1.Milliseconds.HOUR;
            if (lastPortion < 0.001)
                lastPortion = 0;
            for (let t = timestampRange[0]; t < timestampRange[1]; t += iotv_v3_types_1.Milliseconds.HOUR) {
                // Extended by an hour to catch the last CMV if we only need a portion
                const idx = this.idxFromTimestamp(t, cmvKey);
                const cmv = this.getByIndex(idx);
                if (!cmv)
                    continue;
                if (t == timestampRange[0]) {
                    // Scale CMV by portion
                    const scaledCmv = {
                        mean: cmv.mean * firstPortion, // I don't know if this is sound
                        cn: cmv.cn * firstPortion,
                        sqDiffFromMean: cmv.sqDiffFromMean * firstPortion,
                        firstTimestamp: timestampRange[0],
                        lastTimeStamp: cmv.lastTimeStamp,
                        min: cmv.min,
                        max: cmv.max,
                    };
                    cmvs.push(scaledCmv);
                }
                else if (t > timestampRange[1] - iotv_v3_types_1.Milliseconds.HOUR) {
                    if (lastPortion > 0) {
                        //console.log(lastPortion)
                        const scaledCmv = {
                            mean: cmv.mean * lastPortion, // I don't know if this is sound
                            cn: cmv.cn * lastPortion,
                            sqDiffFromMean: cmv.sqDiffFromMean * lastPortion,
                            firstTimestamp: cmv.firstTimestamp,
                            lastTimeStamp: timestampRange[1],
                            min: cmv.min,
                            max: cmv.max,
                        };
                        cmvs.push(scaledCmv);
                    }
                }
                else {
                    cmvs.push(cmv);
                }
            }
            if (cmvs.length == 0)
                return null;
            debug && console.log('CMVs found', cmvs);
            const addedNormal = cmvs.reduce((total, cmv) => {
                return {
                    mean: total.mean + cmv.mean,
                    cn: total.cn + cmv.cn,
                    sqDiffFromMean: (total.sqDiffFromMean ?? 0) + (cmv.sqDiffFromMean ?? 0), // This is completely wrong, idk if we can find this
                    firstTimestamp: (cmv.firstTimestamp ?? Infinity) < (total.firstTimestamp ?? Infinity) ? cmv.firstTimestamp : total.firstTimestamp,
                    lastTimeStamp: (cmv.lastTimeStamp ?? -Infinity) > (total.lastTimeStamp ?? -Infinity) ? cmv.lastTimeStamp : total.lastTimeStamp,
                    min: (cmv.min ?? Infinity) < (total.min ?? Infinity) ? cmv.min : total.min,
                    max: (cmv.max ?? -Infinity) > (total.max ?? -Infinity) ? cmv.max : total.max,
                };
            }, { mean: 0, cn: 0, sqDiffFromMean: 0, firstTimestamp: undefined, lastTimeStamp: undefined, min: Infinity, max: -Infinity });
            const averaged = new CMVarAggregation_1.CMVarAggregationObject({
                ...addedNormal,
                mean: addedNormal.mean / cmvs.length,
                cn: addedNormal.cn / cmvs.length,
            });
            return averaged;
        };
        /**
         * Gets a normal for a defined period, then goes back each outer period and gets the normal for the inner period in that outer period
         *
         * E.g. get 1:00 to 2:00 in each 24 hour period for 3 periods = 1-2 today, 1-2 yesterday, 1-2 the day before. All aggregated
         *
         * @param inner - The time period of the normal in the outerperiod to get
         * @param outerPeriodMs - The period you want to skip backwards to get. Modifys the inner period.
         * @param numPeriods - The number of periods to go back and aggregate from
         * @param readingKey - The key to get a normal for
         */
        this.getNormalForInnerPeriodEveryOuterPeriod = (inner, outerPeriodMs, numPeriods, readingKey) => {
            const debug = false;
            if (inner.toDateMs - inner.fromDateMs > outerPeriodMs) {
                // CANT DO THAT!!
                console.warn('getNormalForInnerPeriodEveryOuterPeriod', 'Inner period is bigger than outer period!');
                return undefined;
            }
            const normal = new CMVarAggregation_1.CMVarAggregationObject();
            let count = 0;
            let countedPeriods = 0;
            for (let i = 0; i < numPeriods; i += 1) {
                const normPeriod = {
                    fromDateMs: inner.fromDateMs - outerPeriodMs * i,
                    toDateMs: inner.toDateMs - outerPeriodMs * i,
                };
                const periodNorm = this.getNormalForPeriod([normPeriod.fromDateMs, normPeriod.toDateMs], readingKey);
                debug && console.log(`Getting normal for ${(0, util_1.getNiceDate)(normPeriod.fromDateMs)} - ${(0, util_1.getNiceDate)(normPeriod.toDateMs)}: ${periodNorm?.mean}`);
                if (periodNorm !== undefined && periodNorm !== null) {
                    normal.update(periodNorm.mean, inner.fromDateMs - outerPeriodMs * i);
                    count += periodNorm.cn;
                    countedPeriods++;
                }
            }
            normal.cn = count;
            if (normal.cn === 0)
                return undefined;
            return { normal, countedPeriods };
        };
        if (!ob) {
            this.type = 'HourlyYearBasedNormalAccumulator';
            this.initialize();
        }
    }
}
exports.HourlyYearBasedNormalAccumulator = HourlyYearBasedNormalAccumulator;
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], HourlyYearBasedNormalAccumulator.prototype, "initialize", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], HourlyYearBasedNormalAccumulator.prototype, "getDimensions", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], HourlyYearBasedNormalAccumulator.prototype, "xFn", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], HourlyYearBasedNormalAccumulator.prototype, "ingest", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], HourlyYearBasedNormalAccumulator.prototype, "egest", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], HourlyYearBasedNormalAccumulator.prototype, "getShortCMVKey", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], HourlyYearBasedNormalAccumulator.prototype, "getOriginalCMVKey", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], HourlyYearBasedNormalAccumulator.prototype, "dehydrate", void 0);
